import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  Observable,
} from '@apollo/client'
import type { Subscription } from 'zen-observable-ts'

const getEnvVariable = (name: string): string => {
  const value = import.meta.env[name]
  if (!value) {
    console.error(`Environment variable ${name} is not defined. Please check your .env file.`)
    if (import.meta.env.DEV) {
      console.error(`For development, create a .env.local file with ${name}=your_value`)
    }
    throw new Error(`Environment variable ${name} is not defined`)
  }
  return value
}

const createAuthUpdateLink = () => {
  return new ApolloLink((operation, forward) => {
    return new Observable(observer => {
      let handle: Subscription
      Promise.resolve(operation)
        .then(oper => {
          if (operation.operationName === 'Logout') {
            const token = localStorage.getItem('authToken')
            if (!token) {
              observer.next({
                data: {
                  userLogout: {
                    success: false,
                    errors: ['Failed to logout']
                  }
                }
              })
              observer.complete()
              return
            }
          }
          return forward(oper)
        })
        .then(observable => {
          if (!observable) return
          handle = observable.subscribe({
            next: result => {
              if (operation.operationName === 'Login' && result.data?.userLogin?.user && result.data?.userLogin?.token) {
                const event = new CustomEvent('loginSuccess', {
                  detail: {
                    user: result.data.userLogin.user,
                    token: result.data.userLogin.token
                  }
                })
                window.dispatchEvent(event)
              }
              if (operation.operationName === 'Logout' && result.data?.userLogout?.success) {
                const event = new CustomEvent('logoutSuccess')
                window.dispatchEvent(event)
              }
              observer.next(result)
            },
            error: error => observer.error(error),
            complete: () => observer.complete()
          })
        })
        .catch(error => observer.error(error))
      return () => {
        if (handle) handle.unsubscribe()
      }
    })
  })
}

export const createApolloClient = () => {
  const authLink = new ApolloLink((operation, forward) => {
    const token = localStorage.getItem('authToken')
    if (token) {
      operation.setContext({headers: {Authorization: `Bearer ${token}`}})
    }
    return new Observable(observer => {
      let handle: Subscription
      Promise.resolve(operation)
        .then(oper => forward(oper))
        .then(observable => {
          handle = observable.subscribe({
            next: result => observer.next(result),
            error: async error => {
              const status = error?.status || error?.networkError?.status
              if (status === 401) {
                try {
                  const response = await fetch(getEnvVariable('VITE_GRAPHQL_API_URL'), {
                    method: 'POST',
                    headers: {
                      'content-type': 'application/json',
                      'accept': '*/*'
                    },
                    body: JSON.stringify({query: 'mutation { refreshToken { token } }'})
                  })
                  const { data } = await response.json()
                  const newToken = data.refreshToken.token
                  localStorage.setItem('authToken', newToken)
                  const oldHeaders = operation.getContext().headers
                  operation.setContext({
                    headers: {
                      ...oldHeaders,
                      Authorization: `Bearer ${newToken}`
                    }
                  })
                  const retryObservable = forward(operation)
                  handle = retryObservable.subscribe({
                    next: result => observer.next(result),
                    error: err => observer.error(err),
                    complete: () => observer.complete()
                  })
                  return
                } catch (refreshError) {
                  observer.error(refreshError)
                }
              } else {
                observer.error(error)
              }
            },
            complete: () => observer.complete()
          })
        })
        .catch(error => observer.error(error))
      return () => {
        if (handle) handle.unsubscribe()
      }
    })
  })

  const httpLink = new HttpLink({uri: getEnvVariable('VITE_GRAPHQL_API_URL')})

  const authUpdateLink = createAuthUpdateLink()

  return new ApolloClient({
    link: ApolloLink.from([authUpdateLink, authLink, httpLink]),
    cache: new InMemoryCache()
  })
}

export const client = createApolloClient()

export default client 


